import { useEffect, useRef, useState } from 'react';

export interface MultiSelectOption {
  value: string;
  label: string;
}

/**
 * A chip/tag multi-select with a searchable dropdown: selected items show as removable chips,
 * the dropdown lists all options (selected ones highlighted), and typing filters them.
 */
export function MultiSelect({
  options, selected, onChange, placeholder, disabled, emptyText,
}: {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  emptyText?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const labelOf = (v: string) => options.find(o => o.value === v)?.label ?? v;
  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter(o => o.label.toLowerCase().includes(q)) : options;

  function toggle(v: string) {
    onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  }

  /** Pick from the list: apply the choice then close the panel (so it hides after selecting). */
  function pick(v: string) {
    toggle(v);
    setQuery('');
    setOpen(false);
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        onClick={() => { if (!disabled) setOpen(true); }}
        style={{
          display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', minHeight: 38,
          padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 8,
          background: disabled ? 'var(--surface-2)' : 'var(--surface)',
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      >
        {selected.map(v => (
          <span key={v} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--surface-2)',
            border: '1px solid var(--border)', borderRadius: 4, padding: '1px 6px', fontSize: 13,
          }}>
            <button type="button" aria-label={`Remove ${labelOf(v)}`}
                    onClick={e => { e.stopPropagation(); toggle(v); }}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, lineHeight: 1, fontSize: 14 }}>
              ×
            </button>
            {labelOf(v)}
          </span>
        ))}
        <input
          value={query}
          disabled={disabled}
          placeholder={selected.length === 0 ? (placeholder ?? 'Select…') : ''}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          style={{ flex: 1, minWidth: 60, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, padding: '2px 0' }}
        />
        <span aria-hidden style={{ opacity: 0.6, paddingLeft: 4 }}>{open ? '▲' : '▾'}</span>
      </div>

      {open && !disabled && (
        <div style={{
          position: 'absolute', zIndex: 50, left: 0, right: 0, marginTop: 4, maxHeight: 220,
          overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 8,
          background: 'var(--surface)', boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        }}>
          {filtered.length === 0 && (
            <div className="muted" style={{ padding: '8px 12px', fontSize: 13 }}>{emptyText ?? 'No options'}</div>
          )}
          {filtered.map(o => {
            const sel = selected.includes(o.value);
            return (
              <div key={o.value} onClick={() => pick(o.value)}
                   style={{
                     padding: '8px 12px', cursor: 'pointer',
                     fontWeight: sel ? 700 : 400,
                     background: sel ? 'var(--surface-2)' : 'transparent',
                   }}>
                {o.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
